<script setup lang="ts">
// Use the official useAuth directly
const { session, user } = useAuth();
const currentUser = computed(() => session.value?.user);
const route = useRoute();

function logout(){
  // Navigate to logout page to handle cleanup process properly
  navigateTo('/logout');
}

function login(){
  navigateTo('/login');
}

function getUserInitials() {
  if (!currentUser.value?.name) return '?';
  
  const nameParts = currentUser.value.name.split(' ');
  if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();
  
  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
}
</script>

<template>
  <div class="user-dropdown">
    <template v-if="currentUser">
      <div class="user-info">
        <div class="user-avatar-container">
          <img v-if="currentUser?.image" :src="currentUser.image" alt="User avatar" class="user-avatar" />
          <div v-else class="user-avatar-placeholder">{{ getUserInitials() }}</div>
        </div>
        <span class="user-name">{{ currentUser?.name }}</span>
        <button @click="logout()" class="auth-button logout">
          <i class="fas fa-sign-out-alt" style="margin-right: 0.5rem;"></i>
          Logout
        </button>
      </div>
    </template>
    <template v-else>
      <button @click="login()" class="auth-button login" :class="(route.path === '/wizard' && !user) ? 'guest-budget-message' : ''">
        <i class="fas fa-sign-in-alt" style="margin-right: 0.5rem;"></i>
        Login
      </button>
    </template>
  </div>
</template>

<style scoped>
.guest-budget-message::after {
  content: 'Sign in without creating a budget';
  opacity: 100%;
  transform: translateY(50px) translateX(-136px);
  padding: 10px;
  border-radius: 25px;
  background-color: var(--color-primary-light);
  z-index: 20000;
  background-image: none;
  width: auto;
  position: absolute;
  animation: bounce 2s ease-in-out infinite;
}

.guest-budget-message::before {
  content: '';
  position: absolute;
  transform: translateY(30px) translateX(15px);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 10px solid var(--color-primary-light);
  z-index: 20001;
  animation: bounce-arrow 2s ease-in-out infinite;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(50px) translateX(-136px);
  }
  40% {
    transform: translateY(45px) translateX(-136px);
  }
  60% {
    transform: translateY(47px) translateX(-136px);
  }
}

@keyframes bounce-arrow {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(30px) translateX(15px);
  }
  40% {
    transform: translateY(25px) translateX(15px);
  }
  60% {
    transform: translateY(27px) translateX(15px);
  }
}

.guest-budget-message {
  overflow: visible;
}

.user-dropdown {
  display: flex;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.user-avatar-container {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 2px solid var(--color-teal-light-alt);
}

.user-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--color-primary-dark) 0%, var(--color-primary-light) 100%);
  color: var(--color-white);
  font-weight: 600;
  font-size: 0.875rem;
}

.user-name {
  font-size: 0.95rem;
  font-weight: 500;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-text-blue-gray-dark);
}

.auth-button {
  padding: 0.6rem 1.25rem;
  border-radius: 6px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
}

.login {
  background-color: var(--color-primary-dark);
  color: var(--color-white);
  box-shadow: 0 2px 6px rgba(0, 121, 107, 0.2);
}

.login:hover {
  background-color: var(--color-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 121, 107, 0.3);
}

.logout {
  background-color: var(--color-primary-bg-alternate);
  color: var(--color-text-blue-gray-dark);
  border: 1px solid var(--color-border-light);
  display: flex;
  align-items: center;
}

.logout:hover {
  background-color: var(--color-border-light);
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Mobile responsive styling */
@media (max-width: 900px) {
  .user-dropdown {
    width: 100%;
  }
  
  .user-info {
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
  }
  
  .user-name {
    max-width: none;
    font-size: 1rem;
    font-weight: 600;
  }
  
  .auth-button {
    width: 100%;
    justify-content: center;
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
  }
  
  .logout {
    background-color: var(--color-primary);
    color: var(--color-white);
    border: none;
  }
  
  .logout:hover {
    background-color: var(--color-primary-dark);
  }
  
  .login {
    width: 100%;
  }

  /* Mobile-specific guest budget message */
  .guest-budget-message::after {
    content: 'Sign in without creating a budget';
    transform: translateY(60px) translateX(0);
    left: 50%;
    margin-left: -120px;
    width: 240px;
    text-align: center;
    font-size: 0.85rem;
    padding: 8px 12px;
    border-radius: 20px;
    white-space: normal;
    line-height: 1.3;
  }

  .guest-budget-message::before {
    transform: translateY(45px) translateX(0);
    left: 50%;
    margin-left: -8px;
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(60px) translateX(0);
    }
    40% {
      transform: translateY(55px) translateX(0);
    }
    60% {
      transform: translateY(57px) translateX(0);
    }
  }

  @keyframes bounce-arrow {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(45px) translateX(0);
    }
    40% {
      transform: translateY(40px) translateX(0);
    }
    60% {
      transform: translateY(42px) translateX(0);
    }
  }
}

@media (max-width: 768px) {
  .user-dropdown {
    width: 100%;
  }
  
  .user-info {
    width: 100%;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
  }
  
  .user-name {
    max-width: none;
    font-size: 1rem;
    font-weight: 600;
  }
  
  .auth-button {
    width: 100%;
    justify-content: center;
    padding: 0.8rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
  }
  
  .logout {
    background-color: var(--color-primary);
    color: var(--color-white);
    border: none;
  }
  
  .logout:hover {
    background-color: var(--color-primary-dark);
  }
  
  .login {
    width: 100%;
  }

  /* Extra small mobile adjustments */
  .guest-budget-message::after {
    content: 'Sign in without creating a budget';
    transform: translateY(55px) translateX(0);
    left: 50%;
    margin-left: -110px;
    width: 220px;
    text-align: center;
    font-size: 0.8rem;
    padding: 6px 10px;
    border-radius: 18px;
    white-space: normal;
    line-height: 1.2;
  }

  .guest-budget-message::before {
    transform: translateY(42px) translateX(0);
    left: 50%;
    margin-left: -8px;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 8px solid var(--color-primary-light);
  }

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(55px) translateX(0);
    }
    40% {
      transform: translateY(50px) translateX(0);
    }
    60% {
      transform: translateY(52px) translateX(0);
    }
  }

  @keyframes bounce-arrow {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(42px) translateX(0);
    }
    40% {
      transform: translateY(37px) translateX(0);
    }
    60% {
      transform: translateY(39px) translateX(0);
    }
  }
}
</style>
